// ── Oversize uploads made smaller (owner 2026-08-26; 5 MB for images AND PDFs, report 280d8c4d) ───────
// Lifted out of DocStorage/UploadWizard.tsx so that Due Diligence's uploads (report 423c70a8) make a big
// file smaller exactly the way the wizard does. Each caller decides its own limit and what to do with a
// file that still doesn't fit.
// IMAGES are re-encoded here in the browser: EXIF orientation honoured, long edge capped at 3000 px, JPEG
// q0.85 stepping down until it fits — a passport photo stays perfectly legible. PDFs have no reliable
// in-browser compressor, so the server re-draws their pages (server/src/services/pdfShrink.ts); shrinkPdf
// asks through the caller's own module door.

/** The picture as a JPEG of at most `maxBytes`, named like the original with .jpg on the end. Throws when
 *  even the lowest quality step is still too big, or the browser cannot read the picture. */
export async function compressImage(f: File, maxBytes: number): Promise<File> {
  let bmp: ImageBitmap;
  try { bmp = await createImageBitmap(f, { imageOrientation: 'from-image' } as any); }
  catch { bmp = await createImageBitmap(f); }
  const LONG = 3000;
  const scale = Math.min(1, LONG / Math.max(bmp.width, bmp.height));
  const w = Math.max(1, Math.round(bmp.width * scale)), h = Math.max(1, Math.round(bmp.height * scale));
  const canvas = document.createElement('canvas'); canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('no canvas');
  ctx.drawImage(bmp, 0, 0, w, h);
  (bmp as any).close?.();
  const jpgName = f.name.replace(/\.[^.]+$/, '') + '.jpg';
  for (const q of [0.85, 0.7, 0.55, 0.4]) {
    const blob: Blob | null = await new Promise(res => canvas.toBlob(res, 'image/jpeg', q));
    if (blob && blob.size <= maxBytes) return new File([blob], jpgName, { type: 'image/jpeg' });
  }
  throw new Error('still too large after compression');
}

/** The server's smaller copy of an oversize PDF, or null when nothing smaller came back (or the server
 *  couldn't do it) — the caller then keeps, or refuses, the original. `send` is the module's own door
 *  (api.docStorageShrinkPdf, api.ddShrinkPdf). */
export async function shrinkPdf(f: File, send: (f: File) => Promise<Blob | null>): Promise<File | null> {
  try {
    const blob = await send(f);
    return blob && blob.size < f.size ? new File([blob], f.name, { type: 'application/pdf' }) : null;
  } catch { return null; }
}
